import { track } from './impersonation-context';

/**
 * The curated, low-cardinality vocabulary of customer-app screens. A `Page Viewed`
 * event's `screen_name` property is one of these values — never the raw URL/`asPath`
 * and never the event name itself. Names follow the `Domain: Screen` convention.
 */
export type ScreenName =
  | 'Auth: Login'
  | 'Auth: Register'
  | 'Auth: Forgot Password'
  | 'Auth: Reset Password'
  | 'Auth: Reset Success'
  | 'Auth: Profile Setup'
  | 'Home'
  | 'Setup: Products'
  | 'Setup: Stimuli'
  | 'Setup: Shipping'
  | 'Setup: Surveys'
  | 'Setup: Instructions'
  | 'Setup: Handling'
  | 'Setup: Audience'
  | 'Setup: Summary'
  | 'Fielding'
  | 'Logistics'
  | 'Insights: Summary'
  | 'Insights: Explore'
  | 'Insights: Scorecard'
  | 'Insights: Crosstabs'
  | 'Insights: Penalty Analysis'
  | 'Insights: Alienation Analysis'
  | 'Insights: Product Groups'
  | 'Insights: Manage Segments'
  | 'Insights: Downloads'
  | 'Insights: Reports'
  | 'Insights: Chat'
  | 'Projects'
  | 'Multi-Project Dashboard: Create'
  | 'Multi-Project Dashboard: Edit'
  | 'Multi-Project Dashboard: Explore'
  | 'Multi-Project Dashboard: Scorecard'
  | 'Multi-Project Dashboard: Crosstabs'
  | 'Multi-Project Dashboard: Penalty Analysis'
  | 'Multi-Project Dashboard: Alienation Analysis'
  | 'Multi-Project Dashboard: Product Groups'
  | 'Multi-Project Dashboard: Manage Segments'
  | 'Multi-Project Dashboard: Reports'
  | 'Multi-Project Dashboard: Downloads'
  | 'Persona Chat'
  | 'Settings: Team'
  | 'Plans & Billing'
  | 'Guided Setup'
  | 'Setup: Test Design'
  | 'Tools: Use Cases'
  | 'Tools: Testing Method Selector'
  | 'Tools: ROI Calculator'
  | 'Tools: Sample Size Calculator'
  | 'Tools: Stat Significance Calculator'
  | 'Survey Preview';

/**
 * The single source of truth mapping a Next.js `router.pathname` (the low-cardinality
 * route *pattern*, not the filled `asPath`) to its curated `screen_name`.
 *
 * - A `ScreenName` value → a `Page Viewed` event fires with that screen.
 * - `null` → an intentional skip (redirect-only route that immediately navigates on).
 * - absent key → unmapped; the tracker no-ops (error/catch-all routes land here). The
 *   exhaustiveness test in `apps/client/src/__tests__/pageScreenCoverage.test.ts` guarantees
 *   every real page is a key or an explicit skip.
 *
 * `Partial<Record<string, …>>` keeps the keys open (any pathname resolves to
 * `ScreenName | null | undefined`) while `ScreenName` constrains the *values*, so a
 * typo'd screen name is a compile error.
 */
export const ROUTE_SCREEN_MAP: Partial<Record<string, ScreenName | null>> = {
  '/': 'Auth: Login',
  '/auth/login': 'Auth: Login',
  '/auth/register': 'Auth: Register',
  '/auth/forgot-password': 'Auth: Forgot Password',
  '/auth/reset-password/heimdall': 'Auth: Reset Password',
  '/auth/reset-success': 'Auth: Reset Success',
  '/auth/profile-setup': 'Auth: Profile Setup',
  '/home': 'Home',
  '/project/[publicId]': null,
  '/project/[publicId]/setup': null,
  '/project/[publicId]/setup/products': 'Setup: Products',
  '/project/[publicId]/setup/stimuli': 'Setup: Stimuli',
  '/project/[publicId]/setup/shipping': 'Setup: Shipping',
  '/project/[publicId]/setup/surveys': 'Setup: Surveys',
  '/project/[publicId]/setup/instructions': 'Setup: Instructions',
  '/project/[publicId]/setup/handling': 'Setup: Handling',
  '/project/[publicId]/setup/audience': 'Setup: Audience',
  '/project/[publicId]/setup/summary': 'Setup: Summary',
  '/project/[publicId]/fielding': 'Fielding',
  '/project/[publicId]/logistics': 'Logistics',
  '/project/[publicId]/insights': null,
  '/project/[publicId]/insights/summary': 'Insights: Summary',
  '/project/[publicId]/insights/explore': 'Insights: Explore',
  '/project/[publicId]/insights/scorecard': 'Insights: Scorecard',
  '/project/[publicId]/insights/crosstabs': 'Insights: Crosstabs',
  '/project/[publicId]/insights/penalty-analysis': 'Insights: Penalty Analysis',
  '/project/[publicId]/insights/alienation-analysis': 'Insights: Alienation Analysis',
  '/project/[publicId]/insights/product-groups': 'Insights: Product Groups',
  '/project/[publicId]/insights/manage-segments': 'Insights: Manage Segments',
  '/project/[publicId]/insights/downloads': 'Insights: Downloads',
  '/project/[publicId]/insights/reports': 'Insights: Reports',
  '/project/[publicId]/insights/chat': 'Insights: Chat',
  '/project': 'Projects',
  '/project/multiple': 'Multi-Project Dashboard: Create',
  '/project/multiple/[publicId]': null,
  '/project/multiple/[publicId]/edit': 'Multi-Project Dashboard: Edit',
  '/project/multiple/[publicId]/explore': 'Multi-Project Dashboard: Explore',
  '/project/multiple/[publicId]/scorecard': 'Multi-Project Dashboard: Scorecard',
  '/project/multiple/[publicId]/crosstabs': 'Multi-Project Dashboard: Crosstabs',
  '/project/multiple/[publicId]/penalty-analysis': 'Multi-Project Dashboard: Penalty Analysis',
  '/project/multiple/[publicId]/alienation-analysis':
    'Multi-Project Dashboard: Alienation Analysis',
  '/project/multiple/[publicId]/product-groups': 'Multi-Project Dashboard: Product Groups',
  '/project/multiple/[publicId]/manage-segments': 'Multi-Project Dashboard: Manage Segments',
  '/project/multiple/[publicId]/reports': 'Multi-Project Dashboard: Reports',
  '/project/multiple/[publicId]/downloads': 'Multi-Project Dashboard: Downloads',
  '/digital-twin-focus-group/[personaPanelId]': 'Persona Chat',
  // Placeholder: scaffolding only. It earns a name when the page earns content.
  '/pulse': null,
  // Same as /pulse: unnamed until the Pulse screens get Amplitude names together.
  '/pulse/usage': null,
  '/teams': 'Settings: Team',
  '/plans-billing': 'Plans & Billing',
  '/setup': 'Guided Setup',
  // Redirect-only: bounces to the first step.
  '/build/[publicId]': null,
  '/build/[publicId]/design': 'Setup: Test Design',
  '/build/[publicId]/handling': 'Setup: Handling',
  '/build/[publicId]/shipping': 'Setup: Shipping',
  '/build/[publicId]/summary': 'Setup: Summary',
  // Placeholder step: nothing to measure yet, and reusing the legacy survey builder's name would
  // inflate that screen with views of a "TBD" page. It earns a name when the step earns content.
  '/build/[publicId]/surveys': null,
  '/use-cases': 'Tools: Use Cases',
  '/testing-method-selector': 'Tools: Testing Method Selector',
  '/roi-calculator': 'Tools: ROI Calculator',
  '/sample-size-calculator': 'Tools: Sample Size Calculator',
  '/stat-significance-calculator': 'Tools: Stat Significance Calculator',
  '/preview/[...slug]': 'Survey Preview',
};

/**
 * Convert a Next.js route pattern segment into a matcher fragment.
 * `[param]` → one path segment; `[...slug]` / `[[...slug]]` → remaining segments.
 */
const patternSegmentToRegExp = (segment: string): string => {
  if (segment.startsWith('[[...') && segment.endsWith(']]')) {
    return '(?:/(.*))?';
  }
  if (segment.startsWith('[...') && segment.endsWith(']')) {
    return '/(.+)';
  }
  if (segment.startsWith('[') && segment.endsWith(']')) {
    return '/([^/]+)';
  }
  return `/${segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`;
};

/**
 * Match a filled pathname (App Router `usePathname()`) against a route pattern
 * that uses Next.js `[param]` / `[...slug]` segments.
 */
export const matchRoutePattern = (pattern: string, pathname: string): boolean => {
  if (pattern === pathname) {
    return true;
  }

  const patternSegments = pattern.split('/').filter(Boolean);
  if (patternSegments.length === 0) {
    return pathname === '/';
  }

  const source = `^${patternSegments.map(patternSegmentToRegExp).join('')}$`;
  return new RegExp(source).test(pathname);
};

/**
 * Resolve a pathname to its screen.
 * Accepts Pages Router `router.pathname` patterns (exact map keys) and App Router
 * filled paths (matched against `[param]` map keys).
 * - `ScreenName` → track it.
 * - `null` → intentional skip.
 * - `undefined` → unmapped (no-op).
 */
export const resolveScreenName = (pathname: string): ScreenName | null | undefined => {
  if (Object.prototype.hasOwnProperty.call(ROUTE_SCREEN_MAP, pathname)) {
    return ROUTE_SCREEN_MAP[pathname];
  }

  for (const [pattern, screen] of Object.entries(ROUTE_SCREEN_MAP)) {
    if (matchRoutePattern(pattern, pathname)) {
      return screen;
    }
  }

  return undefined;
};

interface PageViewedProperties {
  screen_name: ScreenName;
  org_id?: string;
  project_id?: string;
}

/**
 * Emit one curated `Page Viewed` event through the shared `track()` funnel (which
 * auto-stamps impersonation context). Optional context keys are omitted entirely when
 * absent rather than sent as `undefined`.
 */
export const trackPageViewed = ({ screen_name, org_id, project_id }: PageViewedProperties) => {
  track('Page Viewed', {
    screen_name,
    ...(org_id !== undefined && { org_id }),
    ...(project_id !== undefined && { project_id }),
  });
};
